import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts'

function formatCompact(value: number): string {
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (abs >= 1_000_000) return sign + (abs / 1_000_000).toFixed(1) + 'M'
  if (abs >= 1_000) return sign + (abs / 1_000).toFixed(0) + 'k'
  return value.toString()
}

/** Format as integer with commas, no currency symbol (e.g. 538,833) */
function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value)
}

export interface ProfitabilityCardProps {
  currentProfit: number
  previousProfit: number
  percentage: number
  currentLabel: string
  previousLabel: string
  /** Short label for "from X" in subtitle (e.g. "Jan 2025") */
  previousShortLabel?: string
  /** Month abbreviation for label under bar (e.g. "Jan") */
  currentMonthAbbr?: string
  previousMonthAbbr?: string
  /** Optional: use existing snapshot styles (Lexend Deca, gray palette) */
  useSnapshotStyle?: boolean
}

export default function ProfitabilityCard({
  currentProfit,
  previousProfit,
  percentage,
  currentLabel,
  previousLabel,
  previousShortLabel,
  currentMonthAbbr,
  previousMonthAbbr,
  useSnapshotStyle = false,
}: ProfitabilityCardProps) {
  const maxAbs = Math.max(Math.abs(currentProfit), Math.abs(previousProfit))
  const xLimit = maxAbs * 1.2 || 1000

  const currentMonth = currentMonthAbbr ?? currentLabel
  const prevMonth = previousMonthAbbr ?? previousLabel
  const fromLabel = previousShortLabel ?? 'previous period'

  const data = [
    { name: currentMonth, value: currentProfit, isCurrent: true },
    { name: prevMonth, value: previousProfit, isCurrent: false },
  ]

  const isPositive = percentage >= 0
  const percentColor = isPositive ? (useSnapshotStyle ? '#13AB5B' : '#16a34a') : (useSnapshotStyle ? '#E93323' : '#dc2626')
  const barBlue = useSnapshotStyle ? '#2C71F6' : '#2563eb'
  const barGray = '#9ca3af'

  return (
    <div
      className={useSnapshotStyle ? 'bg-white rounded-lg border border-gray-200 p-6' : ''}
      style={
        useSnapshotStyle
          ? { fontFamily: 'Lexend Deca' }
          : {
              padding: 24,
              borderRadius: 16,
              background: '#fff',
              width: '100%',
            }
      }
    >
      <h3 className="text-sm font-medium text-gray-700 mb-4">Profit or loss</h3>

      <div className="text-2xl font-semibold text-gray-900 mb-1">
        {formatNumber(currentProfit)}
      </div>

      <div style={{ color: percentColor, marginBottom: 20 }} className="text-sm flex items-center gap-1">
        {isPositive ? '↑' : '↓'} {Math.abs(percentage)}% from {fromLabel}
      </div>

      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={data} margin={{ top: 8, bottom: 8 }} barCategoryGap="20%">
          <CartesianGrid vertical={false} stroke="rgba(0,0,0,0.05)" />
          <XAxis
            type="category"
            dataKey="name"
            tick={{ fill: '#69697A', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="number"
            domain={[-xLimit, xLimit]}
            tickFormatter={formatCompact}
            tick={{ fill: '#69697A', fontSize: 10 }}
            axisLine={false}
            tickLine={false}
          />
          <ReferenceLine y={0} stroke="#9ca3af" strokeWidth={1} />
          <Tooltip
            formatter={(value: number) => [`$${formatCompact(value)}`, '']}
            contentStyle={{ fontFamily: 'Lexend Deca', fontSize: 12 }}
          />
          <Bar dataKey="value" isAnimationActive={false} radius={[6, 6, 0, 0]}>
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.isCurrent ? barBlue : 'transparent'}
                stroke={entry.isCurrent ? undefined : barGray}
                strokeWidth={entry.isCurrent ? 0 : 2}
                strokeDasharray={entry.isCurrent ? undefined : '6 4'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="mt-1 text-sm text-gray-600 space-y-1">
        <div className="flex items-center gap-2">
          <span className="w-8">{currentMonth}</span>
          <span className="font-medium">{formatCompact(currentProfit)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-8">{prevMonth}</span>
          <span className="font-medium">{formatCompact(previousProfit)}</span>
        </div>
      </div>

      <p className="text-xs text-gray-500 mt-4" style={{ fontFamily: 'Lexend Deca' }}>
        Profit is the amount of money made after paying expenses.
      </p>
    </div>
  )
}
